import dayjs from '../../../utils/dayjs';
import { Badge, Card, DataTable, InlineStack, Link, Text } from '@shopify/polaris';
import Pagination from 'components/pagination';
import SimpleFilter from 'components/simpleFilter';
import AudioPlayer from 'entities/phonelog/components/audio-player';
import __helpers from 'helpers/index';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { CALL_STATUS, CALL_STATUS_KEY, DIRECTION_KEY, TypedPhoneLog, useGetPhonelogs } from 'queries/phonelog.query';
import { PhoneInIcon, PhoneOutIcon } from '@shopify/polaris-icons';
import { ___ } from 'languages/index';

export default function CustomerPhonelog({ user_phonenumber }: { user_phonenumber: string }) {
  const rootSearchPath = window.location.search;

  /**
   * If user apply filter, it will add to URL, then parse URL back to initial state
   */
  let StringQuery: any = __helpers.ExtractUrl(rootSearchPath) || false;

  const initialQuery = {
    query: user_phonenumber,
    page: 1,
    limit: 40,
    sort: 'createdAt:desc',
    disposition: '',
  };

  const [phonelogData, setPhonelogData] = useState<TypedPhoneLog[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [idPlaying, setIdPlaying] = useState(false);
  const currentlyPlayingRef = useRef(null); // Lưu tham chiếu tới audio đang phát

  const [mainQuery, setMainQuery] = useState<any>({
    ...initialQuery,
    ...StringQuery,
  });

  const {
    refetch: getEntities,
    data,
    isLoading: loading,
  } = useGetPhonelogs({
    sort: 'createdAt:desc',
    limit: 20,
    ...mainQuery,
  });

  const onChangePagination = useCallback((numPage: number, limit: number) => {
    setMainQuery((prevMainQuery) => ({ ...prevMainQuery, page: numPage, limit: limit }));
  }, []);

  const renderItem = (phonelogData: TypedPhoneLog) => {
    if (__helpers.isEmpty(phonelogData)) return;
    let { id, destination, answeredBy, createdAt, duration, disposition, source, recordingfile, direction, destination_data, source_data } =
      phonelogData;

    const isInbound = direction === DIRECTION_KEY.INBOUND;
    let tone;
    switch (disposition) {
      case CALL_STATUS_KEY.MISSED:
      case CALL_STATUS_KEY.FAILED:
        tone = 'critical';
        break;
      case CALL_STATUS_KEY.BUSY:
        tone = 'warning';
        break;
      case CALL_STATUS_KEY.NO_ANSWER:
        tone = 'magic';
        break;
      case CALL_STATUS_KEY.UNKNOWN:
      case CALL_STATUS_KEY.ANSWERING:
        tone = 'attention';
        break;

      default:
        tone = 'success';
        break;
    }

    return [
      <div style={{ minWidth: '100px' }}>
        <InlineStack blockAlign="center" gap={'200'} as="div">
          <Text tone="subdued" as="span">
            {isInbound ? <PhoneInIcon width={20} height={20} /> : <PhoneOutIcon width={20} height={20} />}
          </Text>
          <Badge tone={tone}>{CALL_STATUS?.[disposition] || ''}</Badge>
        </InlineStack>
      </div>,

      <Text as="p">
        {source_data ? (
          <div>
            <Link removeUnderline url={`/customer/view/${source_data.user_id}`}>
              <strong>{source_data.display_name || source_data.user_phonenumber}</strong>
            </Link>
          </div>
        ) : (
          <>
            <Badge></Badge>
            <Badge></Badge>
          </>
        )}
        <Text as="h4" variant="headingMd" tone="success">
          {/* <Link removeUnderline url={`tel:${source}`}> */}
          {source || '--'}
          {/* </Link> */}
        </Text>
      </Text>,

      <Text as="p">
        {destination_data ? (
          <div>
            <Link removeUnderline url={`/customer/view/${destination_data.user_id}`}>
              <strong>{destination_data.display_name || destination_data.user_phonenumber}</strong>
            </Link>
          </div>
        ) : (
          <>
            <Badge></Badge>
            <Badge></Badge>
          </>
        )}
        <Text as="h4" variant="headingMd" tone="success">
          {/* <Link removeUnderline url={`tel:${destination}`}> */}
          {destination || ''}
          {/* </Link> */}
        </Text>
      </Text>,

      <Text as="p">{dayjs(new Date(Number(createdAt))).format('DD-MM-YYYY HH:mm')}</Text>,
      <AudioPlayer
        id={id}
        duration={duration}
        idPlaying={idPlaying}
        setIdPlaying={setIdPlaying}
        currentlyPlayingRef={currentlyPlayingRef}
        audioUrl={recordingfile || ''}
      />,
      <Text fontWeight="bold" as="p">
        {answeredBy ? (
          answeredBy
        ) : (
          <>
            <Badge></Badge>
            <Badge></Badge>
          </>
        )}
      </Text>,
    ];
  };

  const filterCallback = useCallback((_value: any) => {
    if (_value === false) {
      return setMainQuery(initialQuery);
    } else {
      setMainQuery((oldValue) => {
        return { ...oldValue, ..._value };
      });
    }
  }, []);

  useEffect(() => {
    try {
      let { body, totalItems } = data;
      setPhonelogData(body);
      setTotalItems(totalItems);
    } catch (e) {}
  }, [data]);

  useEffect(() => {
    getEntities();
  }, [mainQuery]);

  //   useEffect(() => {
  //     let buildURLSearch = __helpers.buildEndUrl(mainQuery);
  //     if (window.location.search !== buildURLSearch) {
  //       window.history.replaceState(null, 'Product', '/phonelog' + buildURLSearch);
  //     }
  //   }, [mainQuery]);

  return (
    <>
      <Card padding="0">
        <SimpleFilter
          loading={loading}
          onCallback={filterCallback}
          options={[
            {
              label: 'GỌI NHỠ',
              field: 'disposition',
              value: 'MISSED',
            },
            {
              label: 'BẬN',
              field: 'disposition',
              value: 'BUSY',
            },
            {
              label: 'KHÔNG TRẢ LỜI',
              field: 'disposition',
              value: 'NO ANSWER',
            },
          ]}
          sortField={[]}
        />
        <DataTable
          verticalAlign="middle"
          sortable={[false, false, false, false, false, false, false, false]}
          defaultSortDirection="descending"
          initialSortColumnIndex={6}
          onSort={null}
          columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text', 'text']}
          headings={['', 'Gọi từ', 'Gọi đến', 'Thời gian', 'Ghi âm', 'Extension']}
          rows={phonelogData?.map(renderItem)}
          hideScrollIndicator
          hasZebraStripingOnData
          increasedTableDensity
          stickyHeader={false} /** Đừng dính cái heading lại, toang đấy! */
          footerContent={___('Show page {display_page_number} of {total_record_number} results', {
            display_page_number: <Text as="strong">{mainQuery?.page || 1}</Text>,
            total_record_number: <Text as="strong">{totalItems}</Text>,
          })}
        />
      </Card>

      <br />
      {totalItems > 0 ? (
        <Pagination
          TotalRecord={totalItems}
          onChange={onChangePagination}
          pageSize={Number(mainQuery?.limit)}
          activeCurrentPage={Number(mainQuery?.page)}
        />
      ) : null}
    </>
  );
}

